import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { fetchTsrFoodPackages, fetchTsrFoodPackageById } from '../../api/tsrFoodApi';
import {
  DEFAULT_FOOD_IMAGE,
  formatAmount,
  mapPackageDetailToState,
  resolveFoodImage
} from '../../utils/orderFoodUtils';
import CustomizePackageModal from '../../components/CustomizePackageModal';

const EllipsisText = ({ text, className = '', style = {} }) => {
  const value = text || '';
  return (
    <div
      className={className}
      style={{
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        minWidth: 0,
        ...style
      }}
      title={value}
    >
      {value}
    </div>
  );
};

function TsrOrderPage() {
  const [searchParams] = useSearchParams();
  const initialCouponCode = searchParams.get('coupon') || '';
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [detailLoading, setDetailLoading] = useState(false);
  const [activePackage, setActivePackage] = useState(null);

  const loadPackages = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await fetchTsrFoodPackages();
      setPackages(response.data || []);
    } catch (err) {
      console.error('Failed to load food packages', err);
      setError('Failed to load food packages. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPackages();
  }, []);

  const handlePackageClick = async (pkg) => {
    try {
      setDetailLoading(true);
      setActivePackage({
        foodPackageId: pkg.FoodPackageId,
        packageName: pkg.PackageName,
        packageImageUrl: resolveFoodImage(pkg),
        persons: Number(pkg.Persons) || 10,
        notes: pkg.Notes || '',
        items: []
      });

      const response = await fetchTsrFoodPackageById(pkg.FoodPackageId);
      const mapped = mapPackageDetailToState(response.data);
      if (!mapped) {
        window.alert('Package details not found.');
        setActivePackage(null);
        return;
      }

      setActivePackage(mapped);
    } catch (err) {
      console.error('Failed to load package details', err);
      window.alert('Failed to load package details. Please try again.');
      setActivePackage(null);
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <div className="container-fluid py-4" style={{ minHeight: '100vh' }}>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4 className="mb-1">Order Food Packages</h4>
          <p className="text-muted mb-0">Browse packages and customize your order.</p>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <span className="spinner-border spinner-border-sm" role="status" />
          <span className="ms-2">Loading packages...</span>
        </div>
      ) : error ? (
        <div className="text-center py-5 text-danger">{error}</div>
      ) : packages.length === 0 ? (
        <div className="card border-0 shadow-sm rounded-4">
          <div className="card-body p-4 text-muted">No packages available yet.</div>
        </div>
      ) : (
        <div className="row g-4">
          {packages.map((pkg) => (
            <div className="col-md-6 col-lg-4 col-xl-3" key={pkg.FoodPackageId}>
              <div
                className="card border-0 shadow-sm h-100"
                style={{ cursor: 'pointer', overflow: 'hidden' }}
                onClick={() => handlePackageClick(pkg)}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    handlePackageClick(pkg);
                  }
                }}
              >
                <img
                  src={resolveFoodImage(pkg)}
                  alt={pkg.PackageName}
                  style={{ height: 140, objectFit: 'cover' }}
                  onError={(event) => {
                    if (event.currentTarget.src.indexOf('tsr-default') === -1) {
                      event.currentTarget.src = DEFAULT_FOOD_IMAGE;
                    }
                  }}
                />
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-start gap-2" style={{ minWidth: 0 }}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <EllipsisText text={pkg.PackageName} className="fw-semibold mb-1" style={{ fontSize: '1rem' }} />
                      <small className="text-muted">{pkg.ItemCount || 0} items</small>
                    </div>
                    <span className="badge bg-success flex-shrink-0">{pkg.Persons} pax</span>
                  </div>
                  <div className="mt-2 fw-semibold text-primary">{formatAmount(pkg.TotalAmount)}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {activePackage ? (
        <CustomizePackageModal
          packageState={activePackage}
          loading={detailLoading}
          initialCouponCode={initialCouponCode}
          onClose={() => setActivePackage(null)}
        />
      ) : null}
    </div>
  );
}

export default TsrOrderPage;
